/**
 * Zugeordnete Fotos je Projekt — prüfen, umhängen, zurück zum Zuordnen.
 *
 * Benjamin, 13.10.2026: „Ich möchte auf der Fotoseite eine einfache Möglichkeit, alle
 * Bilder zu den Projekten zu überblicken und die falsch zugeordneten neu zuzuordnen.“
 * Vorgabe: was aus dem Foto-Eingang kam, der letzten 14 Tage — umschaltbar auf alle.
 */
import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { fehlerText } from "@vimaana/ui";
import {
  useFotosZurueck, useFotoUebersicht, useStapelZuordnen,
  type FotoPhase, type Schritt, type UebersichtProjekt, type ZuordnenProjekt,
} from "../../hooks/useMedien";
import Arbeitsfortschritt from "./Arbeitsfortschritt";
import ProjektSuche from "./ProjektSuche";
import { statusFarbe } from "./FotoKarte";
import { PHASEN, PHASE_TEXT, kurzTag, phaseVorschlag } from "./phase";

export default function FotoUebersicht({ projekte }: { projekte: ZuordnenProjekt[] }) {
  const [quelle, setQuelle] = useState<"eingang" | "alle">("eingang");
  const [tage, setTage] = useState(14);
  const [offen, setOffen] = useState<string | null>(null);
  const liste = useFotoUebersicht(quelle, tage);
  const summe = (liste.data ?? []).reduce((n, p) => n + p.anzahl, 0);

  return (
    <>
      <div className="fo-filter">
        <div className="fo-schalter" role="radiogroup" aria-label="Welche Fotos">
          <button type="button" role="radio" aria-checked={quelle === "eingang"} className={quelle === "eingang" ? "is-an" : ""} onClick={() => setQuelle("eingang")}>Aus dem Foto-Eingang</button>
          <button type="button" role="radio" aria-checked={quelle === "alle"} className={quelle === "alle" ? "is-an" : ""} onClick={() => setQuelle("alle")}>Alle Fotos</button>
        </div>
        <div className="fo-schalter" role="radiogroup" aria-label="Zeitraum">
          <button type="button" role="radio" aria-checked={tage === 14} className={tage === 14 ? "is-an" : ""} onClick={() => setTage(14)}>14 Tage</button>
          <button type="button" role="radio" aria-checked={tage === 0} className={tage === 0 ? "is-an" : ""} onClick={() => setTage(0)}>Alle</button>
        </div>
        {liste.data && <span className="fo-text">{summe} {summe === 1 ? "Foto" : "Fotos"} in {liste.data.length} {liste.data.length === 1 ? "Projekt" : "Projekten"}</span>}
      </div>
      {liste.isLoading && <p className="fo-text">Wird geladen …</p>}
      {liste.isError && <p className="fo-fehler">{fehlerText(liste.error)}</p>}
      {liste.data && liste.data.length === 0 && (
        <p className="fo-text">
          {quelle === "eingang" ? "In diesem Zeitraum wurde nichts aus dem Foto-Eingang zugeordnet." : "Keine Fotos in diesem Zeitraum."}
        </p>
      )}
      <div className="fo-ueb-liste">
        {(liste.data ?? []).map((p) => (
          <ProjektFotos
            key={p.projekt_id}
            p={p}
            offen={offen === p.projekt_id}
            onUmschalten={() => setOffen((o) => (o === p.projekt_id ? null : p.projekt_id))}
            projekte={projekte}
          />
        ))}
      </div>
    </>
  );
}

function ProjektFotos({ p, offen, onUmschalten, projekte }: {
  p: UebersichtProjekt; offen: boolean; onUmschalten: () => void; projekte: ZuordnenProjekt[];
}) {
  const [auswahl, setAuswahl] = useState<Set<string>>(new Set());
  const [ziel, setZiel] = useState<ZuordnenProjekt | null>(null);
  const [phase, setPhase] = useState<FotoPhase | null>(null);
  const [meldung, setMeldung] = useState("");
  const [abbruch, setAbbruch] = useState("");
  const umhaengen = useStapelZuordnen();
  const zurueck = useFotosZurueck();
  const [schritt, setSchritt] = useState<{ s: Schritt; was: string } | null>(null);
  const zuletzt = useRef<Schritt | null>(null);
  const beschaeftigt = umhaengen.isPending || zurueck.isPending;

  function melden(was: string) {
    return (s: Schritt) => { zuletzt.current = s; setSchritt({ s, was }); };
  }
  // Bricht ein Lauf mittendrin ab, ist alles davor schon verschoben — das sagen wir.
  function abgebrochen(e: unknown) {
    const s = zuletzt.current;
    setAuswahl(new Set());
    setAbbruch(
      (s && s.fertig ? `${s.fertig} von ${s.gesamt} Fotos erledigt, dann abgebrochen — die übrigen sind noch hier. ` : "Abgebrochen — kein Foto wurde verschoben. ")
      + fehlerText(e),
    );
  }

  const gewaehlt = p.fotos.filter((f) => auswahl.has(f.id));
  const mitte = useMemo(() => {
    const f = gewaehlt.find((x) => x.breite != null && x.laenge != null);
    return f ? { breite: f.breite!, laenge: f.laenge! } : null;
  }, [gewaehlt]);
  const tag = (gewaehlt[0]?.aufgenommen_am ?? "").slice(0, 10);
  const vorschlag = ziel && tag ? phaseVorschlag(ziel.tage, tag) : null;
  const phaseJetzt = phase ?? vorschlag;

  function fertig(text: string) {
    setMeldung(text);
    setAuswahl(new Set());
    setZiel(null);
    setPhase(null);
  }

  return (
    <article className={`fo-ueb${offen ? " is-offen" : ""}`}>
      <button type="button" className="fo-ueb-kopf" aria-expanded={offen} onClick={onUmschalten}>
        <i style={{ background: statusFarbe(p.status) }} aria-hidden="true" />
        <span>
          <strong>{p.titel || "Projekt"}</strong>
          <small>{p.anzahl} {p.anzahl === 1 ? "Foto" : "Fotos"} · zuletzt {kurzTag(p.zuletzt)}</small>
        </span>
        {!offen && (
          <span className="fo-streifen" aria-hidden="true">
            {p.fotos.slice(0, 5).map((f) => (
              <img key={f.id} src={`/api/kontor/projekt/${p.projekt_id}/aufnahmen/${f.id}/vorschau`} alt="" loading="lazy" />
            ))}
          </span>
        )}
        <em aria-hidden="true">{offen ? "▾" : "▸"}</em>
      </button>
      {meldung && <p className="fo-erledigt-zeile" role="status">✓ {meldung}</p>}
      {abbruch && <p className="fo-fehler fo-abbruch-zeile" role="alert">{abbruch}</p>}
      {offen && (
        <div className="fo-ueb-inhalt">
          <div className="fo-ueb-werkzeug">
            <span className="fo-text">
              {auswahl.size ? `${auswahl.size} ausgewählt` : "Fotos antippen, die woanders hingehören."}
            </span>
            <button type="button" className="vg-btn" onClick={() => setAuswahl(auswahl.size === p.fotos.length ? new Set() : new Set(p.fotos.map((f) => f.id)))}>
              {auswahl.size === p.fotos.length ? "Keine" : "Alle auswählen"}
            </button>
            <Link className="vg-btn" to={`/projekte/${p.projekt_id}`}>Projekt öffnen</Link>
          </div>
          <div className="fo-vorschau fo-ueb-raster">
            {p.fotos.map((f) => {
              const drin = auswahl.has(f.id);
              return (
                <button
                  key={f.id}
                  type="button"
                  className={`fo-bild fo-ueb-bild${drin ? " ist-gewaehlt" : ""}`}
                  aria-pressed={drin}
                  disabled={beschaeftigt}
                  onClick={() => setAuswahl((s) => { const n = new Set(s); if (n.has(f.id)) n.delete(f.id); else n.add(f.id); return n; })}
                >
                  <img src={`/api/kontor/projekt/${p.projekt_id}/aufnahmen/${f.id}/vorschau`} alt="" loading="lazy" />
                  {drin && <i aria-hidden="true">✓</i>}
                  <small>{kurzTag(f.aufgenommen_am)} · {PHASE_TEXT[f.phase as FotoPhase] ?? f.phase}</small>
                </button>
              );
            })}
          </div>
          {auswahl.size > 0 && (
            <div className="fo-ueb-aktion">
              <span className="fo-feld-titel">{auswahl.size} {auswahl.size === 1 ? "Foto" : "Fotos"} umhängen nach</span>
              <ProjektSuche projekte={projekte.filter((x) => x.projekt_id !== p.projekt_id)} mitte={mitte} gewaehlt={ziel} onWahl={(x) => { setZiel(x); setPhase(null); }} />
              {ziel && phaseJetzt && (
                <div className="fo-phasen" role="radiogroup" aria-label="Phase">
                  {PHASEN.map((ph) => (
                    <button key={ph.wert} type="button" role="radio" aria-checked={phaseJetzt === ph.wert}
                      className={phaseJetzt === ph.wert ? "is-an" : ""} onClick={() => setPhase(ph.wert)}>
                      {ph.text}
                    </button>
                  ))}
                </div>
              )}
              {schritt && <Arbeitsfortschritt s={schritt.s} was={schritt.was} />}
              <div className="fo-buendel-aktionen">
                <button
                  type="button"
                  className="vg-btn vg-btn-primary"
                  disabled={!ziel || beschaeftigt}
                  onClick={() => {
                    if (!ziel || !phaseJetzt) return;
                    setMeldung(""); setAbbruch("");
                    umhaengen.mutate(
                      { ids: [...auswahl], projekt_id: ziel.projekt_id, phase: phaseJetzt, umhaengen: true, fortschritt: melden("umgehängt") },
                      {
                        onSuccess: (r) => fertig(`${r.zugeordnet} ${r.zugeordnet === 1 ? "Foto" : "Fotos"} → ${ziel.titel} (${PHASE_TEXT[phaseJetzt]})`),
                        onError: abgebrochen,
                        onSettled: () => setSchritt(null),
                      },
                    );
                  }}
                >
                  {umhaengen.isPending ? `Hängt um … ${schritt ? `${schritt.s.fertig}/${schritt.s.gesamt}` : ""}` : "Umhängen"}
                </button>
                <button
                  type="button"
                  className="vg-btn"
                  disabled={beschaeftigt}
                  onClick={() => {
                    setMeldung(""); setAbbruch("");
                    zurueck.mutate({ ids: [...auswahl], fortschritt: melden("zurückgelegt") }, {
                      onSuccess: (r) => fertig(`${r.zurueck} ${r.zurueck === 1 ? "Foto liegt" : "Fotos liegen"} wieder unter „Zuordnen“`),
                      onError: abgebrochen,
                      onSettled: () => setSchritt(null),
                    });
                  }}
                >
                  {zurueck.isPending ? `Legt zurück … ${schritt ? `${schritt.s.fertig}/${schritt.s.gesamt}` : ""}` : "Zurück zum Zuordnen"}
                </button>
                <button type="button" className="vg-btn" disabled={beschaeftigt} onClick={() => { setAuswahl(new Set()); setZiel(null); }}>Abbrechen</button>
              </div>
            </div>
          )}
        </div>
      )}
    </article>
  );
}
